import React, { Component } from 'react'
import PropTypes from 'prop-types'

/*
 * This is a high order component to centralize all form comum logics.
 */
const FormHOC =
  (options = {}) =>
  (ComposedComponent) => {
    const { fields = [], validate = () => {} } = options

    return class Form extends Component {
      static propTypes = {
        error: PropTypes.object,
      }

      constructor(props) {
        super(props)

        this.state = {
          fields: this.defineFields(props),
          values: this.defineValues(props),
          changed: false,
        }

        this.handleChange = this.handleChange.bind(this)
        this.handleBlur = this.handleBlur.bind(this)
        this.resetForm = this.resetForm.bind(this)
        this.setValueToField = this.setValueToField.bind(this)
      }

      /*
       * Creates an object according to the initial values, for example:
       * - In case initialValues is empty, a field object will be created
       * and filled with blank values, e.g: { username: '' }.
       * - If initialValues has keys, then an object filled with the values will
       * be created.
       */
      defineFields({ initialValues = {} }) {
        const reducer = (obj, name) => ({
          ...obj,
          [name]: {
            input: {
              name,
              onChange: this.handleChange(name),
              onBlur: this.handleBlur(name),
              value: initialValues[name] || '',
              checked: !!initialValues[name] || false,
            },
            error: null,
            touched: false,
            initialValue: initialValues[name],
          },
        })

        return fields.reduce(reducer, {})
      }

      defineValues({ initialValues = {} }) {
        const reducer = (obj, name) => {
          const value = initialValues[name]

          return {
            ...obj,
            [name]: !Number.isNaN(value) ? value : value || '',
          }
        }

        return fields.reduce(reducer, {})
      }

      /*
       * Whenever the component receives props, form errors will be verified.
       * - in case there is an error, this will be verified on any field.
       * - in case there is an error on any field, field and error description will be set
       *   in state (fieldErrors).
       * - in case there are no field errors, e.g: user not found,
       *   form erros will be cleared and the form itself reset.
       */
      componentWillReceiveProps(nextProps) {
        const { error } = nextProps

        if (!error) {
          return
        }

        const { fields } = this.state

        if (error.fields) {
          const reducer = (obj, field) => ({
            ...obj,
            [field.naField]: {
              ...fields[field.naField],
              error: field.dsDetail,
            },
          })

          const newState = error.fields.reduce(reducer, {})

          this.setState({ fields: { ...fields, ...newState } })
        } else {
          this.resetForm()
        }
      }

      componentWillUnmount() {
        this.resetForm()
      }

      /*
       * Input onChange action
       * Whenever there is a change on the input, this will be triggered,
       * adding the value to 'fields'.
       */
      handleChange(key) {
        return (event) => {
          const { fields } = this.state
          const value = this.setValue(event.target)

          this.setState({
            fields: {
              ...fields,
              [key]: {
                ...fields[key],
                input: { ...fields[key].input, value, checked: !!value },
                error: null,
              },
            },
            changed: true,
            values: { ...this.state.values, [key]: value },
          })
        }
      }

      handleBlur(key) {
        return () => {
          const { fields, values } = this.state
          const field = fields[key]
          const error = validate(key, field.input.value, values)

          this.setState({
            ...this.state,
            fields: { ...fields, [key]: { ...field, touched: true, error } },
          })
        }
      }

      /*
       * Sets target value according to type
       */
      setValue(target) {
        switch (target.type) {
          case 'checkbox':
            return target.checked
          case 'file':
            return target.files
          default:
            return target.value
        }
      }

      /*
       * Clears form fields
       */
      resetForm(event) {
        if (event) {
          event.preventDefault()
        }

        this.setState({
          fields: this.defineFields(this.props),
          values: this.defineValues(this.props),
        })
      }

      /*
       * Sets a value to the field
       * @param {String} field
       * @param {String} value
       */
      setValueToField(field, value) {
        const { fields, values } = this.state

        fields[field].input.value = value
        values[field] = value

        this.setState({ fields, values })
      }

      /*
       * If every input on the form is valid, this returns false
       * @return {Boolean}
       */
      get isFormValid() {
        const { fields, values } = this.state

        return !Object.keys(fields).some(
          (field) => !!validate(field, values[field], values)
        )
      }

      render() {
        const newProps = {
          resetForm: this.resetForm,
          setValueToField: this.setValueToField,
          isFormValid: this.isFormValid,
        }

        return (
          <ComposedComponent {...newProps} {...this.props} {...this.state} />
        )
      }
    }
  }

export default FormHOC
